import * as React from 'react';
import IVerificationPage, { IInvestorData } from './IVerificationPage';
import IdentityUploader from '@appComponents/IdentityUploader/IdentityUploader';
import Loader from '@appComponents/Loader/Loader';
import axios from 'axios';
import PeopleVerificationList from '@company/components/PeopleVerificationList/PeopleVerificationList';
import IPersonVerificationStatus from '@company/components/PersonVerificationStatus/IPersonVerificationStatus';
import BttfAxios from '@features/utils/axios';
import ToolTip from '@appComponents/ToolTip/ToolTip';
import QuestionMark from '@appComponents/QuestionMark/QuestionMark';
import { connect } from 'react-redux';
import { TCompanyRootReducer } from '@company/features/index.company';

import * as appStyle from '../../AppStyle.module.scss';
import * as style from './VerificationPageStyle.module.scss'

type TVerificationPageState = {
  loader: boolean;
  identities: any[];
  country: string;
  investorData: IInvestorData ;
  companyCollaborators: IPersonVerificationStatus[];
  companyOwner: IPersonVerificationStatus[];
};

let companyIdentitesTypes: string[] = [
  'shareholder_declaration',
  'articles_of_association',
  'memorandum_of_association',
  'company_bylaws',
  'certificate_of_incorporation',
];

function compare(a: any, b: any) {
  const elemA = a.props.id_type.toUpperCase();
  const elemB = b.props.id_type.toUpperCase();

  let comparison = 0;
  if (elemA > elemB) {
    comparison = 1;
  } else if (elemA < elemB) {
    comparison = -1;
  }
  return comparison;
}

const usTerritoryCountries = {
  US: 'United States of America',
  PR: 'Puerto Rico',
  VI: 'Virgin Islands',
};

export class VerificationPage extends React.Component<IVerificationPage, TVerificationPageState> {
  state: TVerificationPageState = {
    loader: true,
    identities: [],
    country: '',
    investorData: {
      certification_status: '',
      id: 0,
      status: [],
      verification_status: '',
    },
    companyCollaborators: [],
    companyOwner: [],
  };

  UNSAFE_componentWillReceiveProps(nextProps: IVerificationPage) {
    if (nextProps.country !== this.state.country) {
      this.setState({
        country: nextProps.country,
      });
    }
  }

  componentDidMount() {
    const { slug } = this.props.match.params;
    const { getCompanyOwner, getInvestorId, getCompanyColaborators } = this;

    this.setState({
      country: this.props.country,
    });

    BttfAxios.all(
      [getInvestorId(slug), getCompanyOwner(slug), getCompanyColaborators(slug)],
      (investorResponse, companyOwnerResponse, companyCollaboratorsResponse) => {
        const investorData:IInvestorData = investorResponse.data;
        const { id:investorId } = investorData;
        const companyCollaborators:IPersonVerificationStatus[] = companyCollaboratorsResponse.data;
        const companyOwner = companyOwnerResponse.data;

        BttfAxios.get(`/api/investors/${investorId}/identities`, (identities) => {
          const { data } = identities;
          data.forEach((identity: any) => {
            const index = companyIdentitesTypes.indexOf(identity.id_type);
            if (index > -1) {
              companyIdentitesTypes = [
                ...companyIdentitesTypes.slice(0, index),
                ...companyIdentitesTypes.slice(index + 1),
              ];
            }
          });
          this.setState({
            loader: false,
            identities: data,
            investorData: investorData,
            companyOwner: [companyOwner],
            companyCollaborators: companyCollaborators,
          });
        });
      });
  }

  identities = () => {
    const descriptions: { [key:string]: string } = {
      shareholder_declaration: 'Please download this declaration and have it signed by all directors of the entity.',
      memorandum_of_association: 'Please upload the memorandum of association.',
      articles_of_association: 'Please upload the articles of association for the company.',
      company_bylaws: 'Please upload the company bylaws',
      certificate_of_incorporation: 'Upload the certificate of incorporation when the company was' +
      ' registered / tax identification document.',
    };
    const { id:investorId } = this.state.investorData;
    const uploadedIdentites = this.state.identities.map((identity: any) => {
      return (
        <IdentityUploader
          id={identity.id}
          key={identity.id_type}
          id_type={identity.id_type}
          file_url={identity.file_url}
          status={identity.status}
          investorId={investorId}
          comment={identity.comment}
          description={descriptions[identity.id_type]}
          fileSizeMb={20}
        />
      );
    });

    const missingIdentities = companyIdentitesTypes.map((idType: string) => {
      return (
        <IdentityUploader
          id={null}
          key={idType}
          id_type={idType}
          file_url=""
          status=""
          comment=""
          investorId={investorId}
          description={descriptions[idType]}
          fileSizeMb={20}
        />
      );
    });
    let identities = uploadedIdentites.concat(missingIdentities).sort(compare);
    identities = identities.filter((identity) => {
      return Object.keys(usTerritoryCountries).includes(this.state.country) ?
        identity.props.id_type !== 'memorandum_of_association' :
        identity.props.id_type !== 'company_bylaws';
    });

    return this.state.loader ? <Loader /> : identities;
  }

  render() {
    const { companyCollaborators, companyOwner } = this.state;
    return (
      <div className="container">
        <div className={style.verificationContent}>
          <h3 className={style.verificationHeader}>
            <span data-cy="company-status">
              Your company verification status:
            </span>
            {this.verificationStatus()}
          </h3>
          <div className={style.pageDescription}>
            To verify your account all company documents below need to be submited and verified by us.
            All company collaborators also need to verify their accounts by uploading their photo ID and proof of
            residential address on their personal accounts. If the company is verified, but not all members are yet,
            then company
            will remain part verified until completed.
          </div>
          <div className={style.section}>
            <h2 className={appStyle.companySectionHeader}>Company documents</h2>
            <span>
              In order to invest or raise finance for a company or other entity you will need to have a verified company
              account. Please upload all required documents below. This also allows you to setup an e-wallet with our
              partner to collect / invest funds through the platform.
            </span>
            <div className={style.sectionIdentities}>
              {this.identities()}
            </div>
          </div>
          <div className={style.section}>
            <PeopleVerificationList
              slug={this.props.match.params.slug}
              header={'Company owner'}
              peopleList={companyOwner}
              owner={true}
            />
          </div>
          {(companyCollaborators.length > 0) &&
          <div className={style.section}>
            <PeopleVerificationList
              slug={this.props.match.params.slug}
              header={'Company members'}
              peopleList={companyCollaborators}
            />
          </div>
          }
        </div>
      </div>
    );
  }

  private getInvestorId(companyId:string) {
    return axios.get(`/api/companies/${companyId}/verification`);
  }

  private getCompanyOwner(companyId:string) {
    return axios.get(`/api/companies/${companyId}/verification/owner`);
  }

  private getCompanyColaborators(companyId:string) {
    return axios.get(`/api/companies/${companyId}/verification/collaborators`);
  }

  private verificationStatus() {
    const { verification_status } = this.state.investorData;
    let status,description,statusClass = '';
    switch (verification_status) {
      case 'verified':
        status = 'Verified';
        description = 'Your profile documents and income data have been submitted and verified.';
        statusClass = 'verified';
        break;
      case 'awaiting_verification':
        status = 'Awaiting Verification';
        description = 'Your profile documents and income data have been submitted and will be reviewed in' +
          ' the next 5 working days or sooner.';
        statusClass = 'awaiting_verification';
        break;
      case 'incomplete':
        status = 'Incomplete';
        description = 'Some of your documents or income data have not been submitted / verified.' +
          ' Please submit missing documents below.';
        statusClass = 'incomplete';
        break;
      case 'rejected':
        status = 'Rejected';
        description = 'One or more of your documents has not met our criteria.' +
          ' Please resubmit below after reviewing the reason for rejection.';
        statusClass = 'rejected';
        break;
      case 'unverified':
        status = 'Unverified';
        description = `Your company documents and company collaborators have not been verified yet.
         Please submit below.`;
        statusClass = 'unverified';
        break;
      case 'part_verified':
        status = 'Part Verified';
        description = `Your company documents have been verified. Not all company collaborators have been verified yet.
         Please request or upload missing documents from the members below.`;
        statusClass = 'part_verified';
        break;
      case 'fully_verified' :
        status = 'Fully Verified';
        description = 'Congratulations. Your company documents & company collaborators have been verified.';
        statusClass = 'fully_verified';
        break;
      default:
        status = '';
        description = '';
        statusClass = '';
    }
    return (
      <div className={style.statusContainer}>
        <span className={[style.status, style[statusClass]].join(' ')} data-cy="verification-status"> {status}</span>
        <ToolTip text={description} className={[style.statusInfo, 'tooltip-purple'].join(' ')}>
          {description && <QuestionMark marginLeft={5}/>}
        </ToolTip>
      </div>
    );
  }
}

const mapStateToProps = (store: TCompanyRootReducer) => {
  return {
    country: store.companyReducer.companyDetails.location.country,
  };
};

export default connect(mapStateToProps, {})(VerificationPage);

